import cn from 'classnames';
import { useField } from 'formik';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { CountrySelector } from '../../../components/index';
import { selectTaxRequiredCountries } from '../../../store/selectors/shell';
import { ISelectorValue } from '../../../types/shell';
import { getTaxFieldConfig } from '../../../utils/helpers';
import { Input } from '../inputs';

import styles from './ShippingForm.module.scss';

interface IProps {
  isDisabled?: boolean;
  isReadOnly?: boolean;
  className?: string;
  requiredFields?: Record<string, boolean>;
}

const RecipientShippingForm = ({ isDisabled, isReadOnly, className, requiredFields = {} }: IProps) => {
  const taxRequiredCountries = useSelector(selectTaxRequiredCountries);

  const address1Field = useField('receiver_address.address1');
  const address2Field = useField('receiver_address.address2');
  const stateField = useField('receiver_address.state');
  const cityField = useField('receiver_address.city');
  const countryField = useField('receiver_address.country');
  const zipField = useField('receiver_address.zip');
  const taxNumberField = useField('receiver_address.tax_number');
  const companyNameField = useField('receiver_address.company_name');

  const getError = useCallback((field: ReturnType<typeof useField>): string | null => {
    const [_, { touched, error }] = field;
    return touched && error ? error : null;
  }, []);

  const getSelectInputProps = useCallback((field: ReturnType<typeof useField>) => {
    const [{ name, value, onChange, onBlur }, { touched }, { setTouched }] = field;

    return {
      name,
      value,
      onChange: async (_: string, selected: ISelectorValue | null) => {
        const newValue = selected ? selected.value : '';
        const evt = { target: { name, value: newValue } } as React.ChangeEvent<HTMLInputElement>;

        // Mark field as untouched before changing value to prevent premature validation error
        if (!touched && newValue) {
          await setTouched(false);
        }

        onChange(evt);

        // Mark as touched after value is set to trigger validation with the new value
        if (newValue) {
          setTimeout(() => {
            setTouched(true);
          }, 0);
        }
      },
      onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
        onBlur({ target: { name, value: e.target.value } } as React.FocusEvent<HTMLInputElement>);
      },
    };
  }, []);

  const getRequiredField = useCallback(
    (field: ReturnType<typeof useField>) => {
      return requiredFields[field[0].name];
    },
    [requiredFields],
  );

  const taxFieldConfig = useMemo(
    () => getTaxFieldConfig(countryField[0].value, taxRequiredCountries),
    [countryField[0].value, taxRequiredCountries],
  );

  return (
    <div role="form" className={cn(styles.container, className)}>
      <Input
        field={companyNameField[0]}
        helperText="Company name"
        className={styles.inputContainer}
        inputClassName={styles.input}
        placeholder="Company name"
        disabled={isDisabled}
        readOnly={isReadOnly}
        dataAction="input_receiver_address.company_name"
      />
      <Input
        field={address1Field[0]}
        helperText="Address 1"
        className={styles.inputContainer}
        inputClassName={cn(styles.input, { [styles.errorBorder]: !!getError(address1Field) })}
        placeholder="Address 1"
        disabled={isDisabled}
        required={getRequiredField(address1Field)}
        error={getError(address1Field)}
        readOnly={isReadOnly}
        dataAction="input_receiver_address.address1"
      />
      <Input
        field={address2Field[0]}
        helperText="Address 2"
        className={styles.inputContainer}
        inputClassName={cn(styles.input, { [styles.errorBorder]: !!getError(address2Field) })}
        placeholder="Address 2"
        disabled={isDisabled}
        required={getRequiredField(address2Field)}
        error={getError(address2Field)}
        readOnly={isReadOnly}
        dataAction="input_receiver_address.address2"
      />
      <Input
        field={cityField[0]}
        helperText="City"
        className={styles.inputContainer}
        inputClassName={cn(styles.input, { [styles.errorBorder]: !!getError(cityField) })}
        placeholder="City"
        disabled={isDisabled}
        required={getRequiredField(cityField)}
        error={getError(cityField)}
        readOnly={isReadOnly}
        dataAction="input_receiver_address.city"
      />
      <Input
        field={stateField[0]}
        helperText="State"
        className={styles.inputContainer}
        inputClassName={cn(styles.input, { [styles.errorBorder]: !!getError(stateField) })}
        placeholder="State"
        disabled={isDisabled}
        required={getRequiredField(stateField)}
        error={getError(stateField)}
        readOnly={isReadOnly}
        dataAction="input_receiver_address.state"
      />
      <Input
        field={zipField[0]}
        helperText="ZIP code"
        className={styles.inputContainer}
        inputClassName={cn(styles.input, { [styles.errorBorder]: !!getError(zipField) })}
        placeholder="ZIP code"
        disabled={isDisabled}
        required={getRequiredField(zipField)}
        error={getError(zipField)}
        readOnly={isReadOnly}
        dataAction="input_receiver_address.zip"
      />
      <CountrySelector
        {...getSelectInputProps(countryField)}
        containerClassName={cn(styles.inputContainer, styles.inputWidth)}
        className={cn(styles.countrySelect, { [styles.errorBorder]: !!getError(countryField) })}
        label="Country"
        required={getRequiredField(countryField)}
        error={getError(countryField)}
        disabled={isDisabled}
        readOnly={isReadOnly}
        dataAction="input_receiver_address.country"
      />
      {taxFieldConfig?.label && (
        <Input
          field={taxNumberField[0]}
          helperText={taxFieldConfig.label}
          className={styles.inputContainer}
          inputClassName={styles.input}
          placeholder={taxFieldConfig.label}
          disabled={isDisabled}
          required={false}
          readOnly={isReadOnly}
          dataAction="input_receiver_address.tax_number"
        />
      )}
    </div>
  );
};

export default RecipientShippingForm;
